import type { PlayerSnapData, SnapshotData, Vec3Like } from "../wasm/types";
import { PlayerFlags } from "../wasm/types";
import { TICK_DT, TICK_MS, ticks, wholeTicks } from "./constants";
import { LossMeter } from "./stats";

export type InterpMode = "auto" | "low" | "high";

/** A remote player at the render time. Entries in the output map are reused between calls. */
export interface InterpolatedPlayer {
  id: number;
  pos: Vec3Like;
  vel: Vec3Like;
  /** Radians. */
  yaw: number;
  pitch: number;
  flags: number;
  weaponId: string;
  health: number;
}

// Snapshots arrive every tick. The interpolation delay is counted in snapshot intervals (ticks):
// its floor and base are one interval plus a margin, so they shrink with the snapshot rate (what a
// higher rate buys); everything else is a time converted with `ticks`, or a per-snapshot filter
// weight derived from a time constant.

/** Snapshots kept (0.53 s). */
const CAPACITY = wholeTicks(0.53);
/** Extrapolate with the last velocity for this many ticks (50 ms) past the newest snapshot, then hold. */
export const EXTRAPOLATE_TICKS = ticks(0.05);
/** Lowest auto delay: one snapshot interval plus a half (ticks). */
const MIN_DELAY = 1.5;
/** Highest auto delay (ticks; 100 ms). */
const MAX_DELAY = ticks(0.1);
/** Base of the auto delay: one snapshot interval plus a quarter (ticks). */
const BASE_DELAY = 1.25;
/** Auto delay per tick of measured inter-arrival jitter (a ratio: 2.5 ms per ms at any rate). */
const JITTER_DELAY = 2.5;
/** Extra auto delay while snapshot loss is above 1 %: one snapshot interval (ticks). */
const LOSS_BONUS = 1;
/** Fixed `low` delay: the auto base, one snapshot interval plus a quarter (ticks). */
const LOW_DELAY = BASE_DELAY;
/** Fixed `high` delay (ticks; 66.7 ms). */
const HIGH_DELAY = ticks(1 / 15);
/** Auto delay before the first measurement: near the clean-connection target, in snapshot intervals like it (ticks). */
const START_DELAY = 2;
/** The auto delay follows its target with this weight per snapshot: a time constant of 1/3 s. */
const DELAY_WEIGHT = 1 / ticks(1 / 3);
/** The jitter follows each inter-arrival deviation with this weight: a time constant of 0.27 s (RFC 3550's 1/16 per packet at 60 snapshots/s). */
const JITTER_WEIGHT = 1 / ticks(16 / 60);
/** The snapshot clock follows early arrivals with this weight per snapshot (time constant 1/6 s)... */
const EARLY_WEIGHT = 1 / ticks(1 / 6);
/** ...and late ones with this one (time constant 5/6 s), so jitter does not move it. */
const LATE_WEIGHT = 1 / ticks(5 / 6);
/** The snapshot clock jumps to the measurement when it is off by more than this many ticks (250 ms). */
const RESYNC_TICKS = ticks(0.25);
/** The render clock holds instead of moving back by less than this many ticks (33 ms). */
const MAX_HELD_REGRESSION = ticks(1 / 30);
/**
 * Moves beyond this between two snapshots (respawn, teleport) are not interpolated. A distance, not
 * a speed: no movement covers it in the few snapshot intervals a loss leaves between two snapshots.
 */
const TELEPORT_M = 12;
const TAU = Math.PI * 2;

const stamps = new WeakMap<InterpolatedPlayer, number>();

function newEntry(id: number): InterpolatedPlayer {
  return { id, pos: { x: 0, y: 0, z: 0 }, vel: { x: 0, y: 0, z: 0 }, yaw: 0, pitch: 0, flags: 0, weaponId: "", health: 0 };
}

function findPlayer(list: PlayerSnapData[], id: number): PlayerSnapData | undefined {
  for (let i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
  return undefined;
}

/** Shortest-arc interpolation between two angles in radians; the result is in `[0, 2π)`. */
export function lerpAngle(a: number, b: number, t: number): number {
  let d = b - a;
  d -= TAU * Math.round(d / TAU);
  let r = (a + d * t) % TAU;
  if (r < 0) r += TAU;
  if (r >= TAU) r = 0;
  return r;
}

/**
 * Snapshot interpolation of the remote players (docs/NETCODE.md "Remote entity interpolation").
 *
 * The render time is `snapshot_clock - interp_delay`. The snapshot clock is the server tick
 * timeline reconstructed from the arrivals: it follows the earliest arrivals quickly and the late
 * ones slowly, so jitter does not move it. The delay adapts to the measured inter-arrival jitter
 * and snapshot loss (`auto`), or is fixed (`low` 1.25 ticks, `high` 8 ticks: 67 ms).
 */
export class SnapshotBuffer {
  private readonly snaps: SnapshotData[] = [];
  private mode: InterpMode;
  /** `server tick - local ms / TICK_MS`, smoothed. */
  private offset = 0;
  private hasOffset = false;
  private lastSample = 0;
  /** RFC 3550 style inter-arrival jitter, in ticks. */
  private jitter = 0;
  /** Current interpolation delay in ticks. */
  private delay = START_DELAY;
  private readonly loss = new LossMeter();
  private stamp = 0;
  private lastRender = Number.NEGATIVE_INFINITY;

  constructor(mode: InterpMode = "auto") {
    this.mode = mode;
    this.applyFixedDelay();
  }

  /** Changes the interpolation setting (`settings.network.interpolation`). */
  setMode(mode: InterpMode): void {
    this.mode = mode;
    this.applyFixedDelay();
  }

  private applyFixedDelay(): void {
    if (this.mode === "low") this.delay = LOW_DELAY;
    else if (this.mode === "high") this.delay = HIGH_DELAY;
  }

  /** Forgets everything (new match or reconnect). */
  clear(): void {
    this.snaps.length = 0;
    this.hasOffset = false;
    this.jitter = 0;
    this.loss.reset();
    this.lastRender = Number.NEGATIVE_INFINITY;
    if (this.mode === "auto") this.delay = START_DELAY;
  }

  latest(): SnapshotData | null {
    return this.snaps.length > 0 ? this.snaps[this.snaps.length - 1] : null;
  }

  interpDelayMs(): number {
    return this.delay * TICK_MS;
  }

  push(s: SnapshotData, arrivalMs: number): void {
    const snaps = this.snaps;
    const n = snaps.length;
    if (n > 0 && s.tick <= snaps[n - 1].tick) {
      // Late: it only fills a hole; it says nothing about the clock.
      let i = n - 1;
      while (i >= 0 && snaps[i].tick > s.tick) i--;
      if (i >= 0 && snaps[i].tick === s.tick) return;
      if (i < 0 && n >= CAPACITY) return;
      snaps.splice(i + 1, 0, s);
      if (snaps.length > CAPACITY) snaps.shift();
      return;
    }
    snaps.push(s);
    if (snaps.length > CAPACITY) snaps.shift();

    const sample = s.tick - arrivalMs / TICK_MS;
    if (!this.hasOffset) {
      this.offset = sample;
      this.hasOffset = true;
    } else {
      this.jitter += (Math.abs(sample - this.lastSample) - this.jitter) * JITTER_WEIGHT;
      const diff = sample - this.offset;
      if (Math.abs(diff) > RESYNC_TICKS) {
        this.offset = sample;
        this.lastRender = Number.NEGATIVE_INFINITY;
      } else {
        this.offset += diff * (diff > 0 ? EARLY_WEIGHT : LATE_WEIGHT);
      }
    }
    this.lastSample = sample;
    this.loss.record(s.tick);
    this.updateDelay();
  }

  private updateDelay(): void {
    if (this.mode !== "auto") return;
    const bonus = this.loss.percent() > 1 ? LOSS_BONUS : 0;
    const target = Math.min(MAX_DELAY, Math.max(MIN_DELAY, BASE_DELAY + JITTER_DELAY * this.jitter + bonus));
    this.delay += (target - this.delay) * DELAY_WEIGHT;
  }

  /** Fractional server tick remote entities are rendered at (0 before the first snapshot). */
  renderTick(nowMs: number): number {
    if (!this.hasOffset) return 0;
    let t = this.offset + nowMs / TICK_MS - this.delay;
    // Small regressions (the clock estimate slowly moving back) would show as stutter.
    if (t < this.lastRender && this.lastRender - t < MAX_HELD_REGRESSION) t = this.lastRender;
    this.lastRender = t;
    return t;
  }

  /**
   * Fills `out` with every remote player of the snapshot interval around `renderTick`, reusing
   * the entries already in the map and removing the ones that are gone. Positions and view angles
   * are interpolated (yaw along the shortest arc); flags, weapon and health switch when the render
   * time reaches the snapshot that introduced them. Past the newest snapshot the last velocity is
   * extrapolated for up to `EXTRAPOLATE_TICKS` (50 ms), then the player holds.
   */
  samplePlayers(renderTick: number, out: Map<number, InterpolatedPlayer>): void {
    const snaps = this.snaps;
    const n = snaps.length;
    if (n === 0) {
      out.clear();
      return;
    }
    const stamp = ++this.stamp;
    let count = 0;

    let i = n - 1;
    while (i >= 0 && snaps[i].tick > renderTick) i--;

    if (i >= 0 && i < n - 1) {
      const a = snaps[i];
      const b = snaps[i + 1];
      const alpha = (renderTick - a.tick) / (b.tick - a.tick);
      for (const pb of b.players) {
        const pa = findPlayer(a.players, pb.id);
        const e = this.entry(out, pb.id, stamp);
        if (pa) blend(e, pa, pb, alpha);
        else copy(e, pb, 0);
        count++;
      }
    } else {
      // Before the oldest snapshot: hold it. After the newest: extrapolate, then hold.
      const s = i < 0 ? snaps[0] : snaps[n - 1];
      const ahead = i < 0 ? 0 : Math.min(Math.max(renderTick - s.tick, 0), EXTRAPOLATE_TICKS);
      for (const p of s.players) {
        copy(this.entry(out, p.id, stamp), p, ahead);
        count++;
      }
    }
    if (out.size !== count) {
      for (const [id, e] of out) if (stamps.get(e) !== stamp) out.delete(id);
    }
  }

  private entry(out: Map<number, InterpolatedPlayer>, id: number, stamp: number): InterpolatedPlayer {
    let e = out.get(id);
    if (!e) {
      e = newEntry(id);
      out.set(id, e);
    }
    stamps.set(e, stamp);
    return e;
  }
}

/** Writes `p` advanced by `ticks` of its own velocity. */
function copy(e: InterpolatedPlayer, p: PlayerSnapData, ticks: number): void {
  const dt = ticks * TICK_DT;
  e.pos.x = p.pos.x + p.vel.x * dt;
  e.pos.y = p.pos.y + p.vel.y * dt;
  e.pos.z = p.pos.z + p.vel.z * dt;
  e.vel.x = p.vel.x;
  e.vel.y = p.vel.y;
  e.vel.z = p.vel.z;
  e.yaw = p.yaw;
  e.pitch = p.pitch;
  e.flags = p.flags;
  e.weaponId = p.weaponId;
  e.health = p.health;
}

/** `a` to `b` at `alpha` (0..1). Discrete fields stay `a`'s until the render time reaches `b`. */
function blend(e: InterpolatedPlayer, a: PlayerSnapData, b: PlayerSnapData, alpha: number): void {
  const dx = b.pos.x - a.pos.x;
  const dy = b.pos.y - a.pos.y;
  const dz = b.pos.z - a.pos.z;
  const jump =
    ((a.flags ^ b.flags) & PlayerFlags.ALIVE) !== 0 || dx * dx + dy * dy + dz * dz > TELEPORT_M * TELEPORT_M;
  const t = jump ? 0 : alpha;
  e.pos.x = a.pos.x + dx * t;
  e.pos.y = a.pos.y + dy * t;
  e.pos.z = a.pos.z + dz * t;
  e.vel.x = a.vel.x + (b.vel.x - a.vel.x) * alpha;
  e.vel.y = a.vel.y + (b.vel.y - a.vel.y) * alpha;
  e.vel.z = a.vel.z + (b.vel.z - a.vel.z) * alpha;
  e.yaw = lerpAngle(a.yaw, b.yaw, alpha);
  e.pitch = a.pitch + (b.pitch - a.pitch) * alpha;
  e.flags = a.flags;
  e.weaponId = a.weaponId;
  e.health = a.health;
}
